<template>
<div>
    <p class="text-muted small">
        NIS Claim for Sickness Benefit + Medical Certificate (B700F6 SB R0).
        Patient details auto-fill from demographics; the doctor completes the
        Medical Certificate section.
    </p>

    <b-form @submit.prevent="onSubmit" novalidate>
        <h6>Claim — patient particulars</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Full name"><b-form-input v-model="form.name" /></b-form-group>
                <b-form-group label="Address"><b-form-textarea rows="2" v-model="form.address" /></b-form-group>
                <b-form-group label="National Insurance number"><b-form-input v-model="form.nis_number" /></b-form-group>
                <b-form-group label="Employer (when ill)"><b-form-input v-model="form.employer" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Occupation"><b-form-input v-model="form.occupation" /></b-form-group>
                <b-row>
                    <b-col><b-form-group label="Finished work on"><b-form-datepicker v-model="form.finished_date" /></b-form-group></b-col>
                    <b-col><b-form-group label="Time">
                        <b-input-group>
                            <b-form-input v-model="form.finished_time" placeholder="e.g. 16:00" />
                            <b-form-select v-model="form.finished_ampm" :options="['a.m.','p.m.']" style="max-width:90px" />
                        </b-input-group>
                    </b-form-group></b-col>
                </b-row>
                <b-form-group label="Industrial accident date (if any)"><b-form-datepicker v-model="form.accident_date" /></b-form-group>
                <b-form-group label="Claim date"><b-form-datepicker v-model="form.claim_date" /></b-form-group>
            </b-col>
        </b-row>

        <h6 class="mt-3">Medical Certificate</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Practitioner name"><b-form-input v-model="form.practitioner_name" /></b-form-group>
                <b-form-group label="Practitioner address"><b-form-input v-model="form.practitioner_address" /></b-form-group>
                <b-form-group label="Examined on (date)"><b-form-datepicker v-model="form.exam_date" /></b-form-group>
                <b-form-group label="Examined at (time + a.m./p.m.)">
                    <b-input-group>
                        <b-form-input v-model="form.exam_time" placeholder="e.g. 10:30" />
                        <b-form-select v-model="form.exam_ampm" :options="['a.m.','p.m.']" style="max-width:90px" />
                    </b-input-group>
                </b-form-group>
                <b-form-group label="Location of examination"><b-form-input v-model="form.exam_location" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Visit number">
                    <b-form-radio-group v-model="form.visit_no" :options="[{value:'first',text:'First'},{value:'second',text:'Second'}]" />
                </b-form-group>
                <b-form-group label="Patient is suffering from"><b-form-textarea rows="2" v-model="form.diagnosis" /></b-form-group>
                <b-form-group label="Outcome">
                    <b-form-radio-group v-model="form.outcome" stacked :options="[
                        { value: 'fit', text: 'Will be fit to resume work' },
                        { value: 'incapable', text: 'Will remain incapable of work for a period of' }
                    ]" />
                </b-form-group>
                <b-row>
                    <b-col v-if="form.outcome === 'fit'">
                        <b-form-group label="Fit to resume on">
                            <b-form-datepicker v-model="form.fit_date" />
                        </b-form-group>
                    </b-col>
                    <b-col v-else>
                        <b-form-group label="Incapable for (days)">
                            <b-form-input type="number" v-model="form.incapable_days" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-form-group label="Doctor's remarks"><b-form-textarea rows="2" v-model="form.remarks" /></b-form-group>
            </b-col>
        </b-row>

        <div class="text-right mt-3">
            <b-button variant="secondary" @click="$emit('close')">Cancel</b-button>
            <b-button type="submit" variant="success">
                <i class="fa-solid fa-file-arrow-down"></i> Generate PDF
            </b-button>
        </div>
    </b-form>
</div>
</template>

<script>
import { formatDate, buildAddress, fullName, loadCoords } from "./pdfFiller"
import formPersistence from "./formPersistence"

export default {
    name: "nis-sickness-form",
    mixins: [formPersistence],
    props: { demographics: { type: Object, default: () => ({}) } },
    data() {
        return {
            form: {
                name: "",
                address: "",
                nis_number: "",
                employer: "",
                occupation: "",
                finished_date: "",
                finished_time: "",
                finished_ampm: "a.m.",
                accident_date: "",
                claim_date: new Date().toISOString().slice(0, 10),

                practitioner_name: "",
                practitioner_address: "",
                exam_date: new Date().toISOString().slice(0, 10),
                exam_time: "",
                exam_ampm: "a.m.",
                exam_location: "",
                visit_no: "first",
                diagnosis: "",
                outcome: "fit",
                fit_date: "",
                incapable_days: "",
                remarks: "",
            },
        }
    },
    watch: {
        demographics: {
            immediate: true,
            handler(d) {
                if (!d) return
                if (!this.form.name) this.form.name = fullName(d)
                if (!this.form.address) this.form.address = buildAddress(d)
                if (!this.form.employer && d.employer) this.form.employer = d.employer?.name || d.employer
                if (!this.form.occupation && d.profession) this.form.occupation = d.profession
                if (!this.form.nis_number && d.nis_number) this.form.nis_number = d.nis_number
            },
        },
    },
    methods: {
        // Approximate coords for nis-sickness-benefit.pdf (left column of the
        // two-column claim layout + right page for medical certificate).
        buildCoords() {
            return {
                name: { page: 0, x: 200, y: 700 },
                address: { page: 0, x: 110, y: 670 },
                nis_number: { page: 0, x: 230, y: 638 },
                employer: { page: 0, x: 170, y: 608 },
                occupation: { page: 0, x: 165, y: 578 },
                finished_date: { page: 0, x: 220, y: 548 },
                finished_time: { page: 0, x: 320, y: 548 },
                accident_date: { page: 0, x: 260, y: 518 },
                claim_date: { page: 0, x: 80, y: 450 },

                practitioner_name: { page: 1, x: 80, y: 700 },
                patient_name: { page: 1, x: 80, y: 670 },
                patient_address: { page: 1, x: 80, y: 645 },
                exam_date: { page: 1, x: 175, y: 615 },
                exam_time_ampm: { page: 1, x: 320, y: 615 },
                exam_location: { page: 1, x: 110, y: 588 },
                visit_first_check: { page: 1, x: 280, y: 588, type: "check" },
                visit_second_check: { page: 1, x: 318, y: 588, type: "check" },
                diagnosis: { page: 1, x: 320, y: 562 },
                fit_check: { page: 1, x: 178, y: 510, type: "check" },
                incapable_check: { page: 1, x: 178, y: 460, type: "check" },
                fit_date: { page: 1, x: 330, y: 510 },
                incapable_days: { page: 1, x: 330, y: 460 },
                remarks: { page: 1, x: 240, y: 420 },
            }
        },
        buildValues() {
            const f = this.form
            return {
                name: f.name,
                address: f.address,
                nis_number: f.nis_number,
                employer: f.employer,
                occupation: f.occupation,
                finished_date: formatDate(f.finished_date),
                finished_time: `${f.finished_time || ""} ${f.finished_ampm || ""}`.trim(),
                accident_date: formatDate(f.accident_date),
                claim_date: formatDate(f.claim_date),

                practitioner_name: f.practitioner_name,
                patient_name: f.name,
                patient_address: f.address,
                exam_date: formatDate(f.exam_date),
                exam_time_ampm: `${f.exam_time || ""} ${f.exam_ampm || ""}`.trim(),
                exam_location: f.exam_location,
                visit_first_check: f.visit_no === "first",
                visit_second_check: f.visit_no === "second",
                diagnosis: f.diagnosis,
                fit_check: f.outcome === "fit",
                incapable_check: f.outcome === "incapable",
                fit_date: f.outcome === "fit" ? formatDate(f.fit_date) : "",
                incapable_days: f.outcome === "incapable" ? f.incapable_days : "",
                remarks: f.remarks,
            }
        },
        async onSubmit() {
            try {
                const coords = (await loadCoords("nis-sickness-benefit")) || this.buildCoords()
                await this.fillAndPersist({ form_key: "nis-sickness", title: "NIS Sickness Benefit" }, {
                    templateUrl: "/forms/nis-sickness-benefit.pdf",
                    outputName: `nis-sickness-${this.form.name || "patient"}.pdf`,
                    values: this.buildValues(),
                    coords,
                })
                this.$emit("close")
            } catch (e) {
                this.$bvToast.toast(e.message || "Could not generate PDF", {
                    title: "PDF generation failed",
                    variant: "danger",
                })
            }
        },
    },
}
</script>
